import React from 'react';
import { Link } from 'react-router-dom';

import { Headercomponent } from '../components/headercomponent';
import { Footer } from '../components/Footer';
import NodeBackground from '../components/nodeBackground';

const clientTypes = [
  {
    title: 'Executives and Public-Facing Leaders',
    text: 'For leaders whose visibility, decisions, public profile, wealth, employment role, or online attention create a need for recurring intelligence support.',
  },
  {
    title: 'Company Security Leads',
    text: 'For security leaders who need a protective intelligence capability without immediately hiring, training, and managing an internal analyst team.',
  },
  {
    title: 'Family Offices and Private Advisors',
    text: 'For trusted advisors supporting privacy, travel, residential, public exposure, and security decisions for high-profile or high-net-worth clients.',
  },
  {
    title: 'Protective Firms',
    text: 'For executive protection firms that want a dedicated intelligence arm to support travel, events, client exposure, and ongoing threat awareness.',
  },
];

const painPoints = [
  {
    title: 'The client feels exposed but lacks insight into their threat picture.',
    text: 'The concern may be real, but without structured intelligence, it can be difficult to separate noise from security-relevant information. We create custom collection and evaluation plans to form a wholistic threat picture and inform their protective positioning.',
  },
  {
    title: 'The internet is too large to monitor manually.',
    text: 'Threats and concerning narratives may appear in comment sections, smaller blogs, forums, social media posts, and other places that ordinary searches miss. With AI and social media, posting threatening content has never been easier.',
  },
  {
    title: 'Security decisions need evidence.',
    text: 'Executives, boards, advisors, and protection teams often need written intelligence to explain why additional security, travel changes, or protective measures are warranted. Understanding a specific threat picture can help plan for the correct level of security.',
  },
  {
    title: 'Traditional protection is stronger with intelligence.',
    text: 'A physical security detail can be highly capable, but without OSINT and protective intelligence, it may miss online warning signs that should inform planning.',
  },
];

const servicesIncluded = [
  {
    title: 'Dedicated Analyst Support',
    text: 'Recurring analyst support for clients and security teams that need an external intelligence capability without building an in-house team.',
  },
  {
    title: 'Initial Online Exposure Assessment',
    text: 'A baseline review of publicly available information, online exposure, family exposure, and security-relevant digital footprint concerns.',
  },
  {
    title: 'Physical Threat Detection',
    text: 'Monitoring and review of online content for physical threat indicators, concerning language, escalation, fixation, and relevant narratives.',
  },
  {
    title: 'Daily Dox Detection',
    text: 'Daily review for doxxing-related exposure and publicly available information that may create privacy or security concerns.',
  },
  {
    title: 'Social Media Review',
    text: 'Review of the client’s public social media presence and relevant posts about the client, company, family, or leadership team.',
  },
  {
    title: 'Forum, Blog, and Comment Section Review',
    text: 'Use of proprietary technology and analyst review to surface relevant content from places often missed by basic search engines.',
  },
  {
    title: 'Travel Assessments',
    text: 'Assessment of hotels, venues, neighborhoods, local concerns, crime patterns, demonstrations, and travel-specific security considerations.',
  },
  {
    title: 'Crime Mapping',
    text: 'Crime mapping included in travel documents, residential reviews, security assessments, and location-specific intelligence products.',
  },
  {
    title: 'Event Intelligence Support',
    text: 'Support for public appearances, private events, conferences, speaking engagements, and moments where exposure or attention may increase.',
  },
  {
    title: 'PAI Takedown Support',
    text: 'Assistance identifying publicly available information and supporting takedown requests where appropriate.',
  },
  {
    title: 'Family Exposure Review',
    text: 'Review of public exposure related to family members where that exposure may affect privacy, travel, residential, or protective considerations.',
  },
  {
    title: 'Weekly Intelligence Reporting',
    text: 'Written reporting that turns findings into concise intelligence products for the client, advisor, or security team.',
  },
];

const intelligenceQuestions = [
  'What public information exists about the client, family, company, or leadership team?',
  'Are there online narratives, grievances, or posts that may create protective concerns?',
  'Is the client being discussed in forums, comment sections, smaller blogs, or other overlooked online spaces?',
  'Are there signs of fixation, escalation, or concerning behavioral indicators online?',
  'What should the security team understand before travel, events, or public appearances?',
  'What crime, venue, hotel, neighborhood, or local context should be reviewed before movement?',
  'What information could help explain the need for protective coverage to a board, advisor, or decision maker?',
  'What changed this week that the client or security team should know?',
];

const deliverables = [
  {
    title: 'Weekly PDF Intelligence Report',
    text: 'Short, focused reporting designed to keep the client and security team informed without overwhelming them with raw data.',
  },
  {
    title: 'Monthly Briefing Call',
    text: 'A recurring briefing to explain intelligence findings, answer questions, and discuss relevance with the client or security team.',
  },
  {
    title: 'Online Exposure Summary',
    text: 'A structured assessment of public exposure, digital footprint concerns, and privacy-relevant information.',
  },
  {
    title: 'Threat Summary',
    text: 'Written findings on relevant online activity, physical threat indicators, concerning narratives, and escalation patterns.',
  },
  {
    title: 'Travel Intelligence Products',
    text: 'Hotel, venue, city, and neighborhood assessments supported by crime mapping and local context.',
  },
  {
    title: 'Security Team Interface',
    text: 'Direct support for protective teams to explain intelligence impact and answer operationally relevant questions.',
  },
];

const retainerTiers = [
  {
    title: 'Core Protective Intelligence Retainer',
    label: 'Entry retainer',
    text: 'For clients or security teams that need recurring intelligence support, baseline exposure review, travel intelligence, and weekly reporting.',
    details: [
      'Dedicated analyst support',
      '50 analyst hours per month',
      'Initial online exposure assessment',
      'Daily dox detection',
      'Physical online threat detection',
      'Two travel assessments per month',
      'One event intelligence support package per quarter',
      'Weekly PDF intelligence report',
      'One briefing call per month',
      'Additional analytic support available hourly after monthly hours are used',
    ],
  },
  {
    title: 'Enhanced Protective Intelligence Retainer',
    label: 'Expanded support',
    text: 'For clients with higher visibility, frequent travel, family exposure concerns, or security teams that need more consistent analyst access.',
    details: [
      'Expanded monthly analyst support',
      'Ongoing online exposure review',
      'Family exposure review',
      'Weekly intelligence reporting',
      'Travel and venue assessments',
      'Event intelligence support',
      'Security team briefing support',
      'Priority intelligence requests',
      'Coordination with trusted advisors or protection leads',
      'Custom reporting cadence when needed',
    ],
  },
  {
    title: 'Dedicated Intelligence Arm',
    label: 'Premium annual engagement',
    text: 'For clients and organizations that need Weathervane to function as an external intelligence department supporting protection, travel, exposure, and reporting needs.',
    details: [
      'Two-analyst support model',
      'Ongoing protective intelligence coverage',
      'Frequent reporting and briefings',
      'Travel, venue, and event support',
      'Exposure and dox monitoring',
      'Family exposure review',
      'Security team coordination',
      'Board or executive-level reporting support',
      'Custom intelligence workflow',
      'Designed for long-term protective intelligence maturity',
    ],
  },
];

const processSteps = [
  {
    number: '01',
    title: 'Scope the intelligence requirement.',
    text: 'We begin by understanding the client profile, public exposure, security concerns, travel tempo, family considerations, and existing protection structure.',
  },
  {
    number: '02',
    title: 'Build the baseline threat and exposure picture.',
    text: 'We review public exposure, online discussion, relevant social media, doxxing indicators, crime context, and other open-source information that may matter to protection.',
  },
  {
    number: '03',
    title: 'Monitor, analyze, and report.',
    text: 'Analysts use structured OSINT methods and proprietary technology to surface relevant findings, then convert them into concise intelligence reporting.',
  },
  {
    number: '04',
    title: 'Brief the security team or client.',
    text: 'Findings are explained in a briefing-style format so the client, advisor, or protection team understands what changed and why it matters.',
  },
];

const whyWeathervane = [
  {
    title: 'Protective intelligence experience',
    text: 'Weathervane is built around protective intelligence workflows, not generic monitoring or keyword alerts.',
  },
  {
    title: 'Law enforcement background',
    text: 'The company’s founder brings prior Washington, D.C. law enforcement experience, including narcotics, weapons, and gang-related work.',
  },
  {
    title: 'OSINT analysis background',
    text: 'The work is grounded in open-source intelligence analysis and written intelligence products designed to support security decisions.',
  },
  {
    title: 'Technology-enabled collection',
    text: 'Proprietary software helps speed collection, reduce manual screening time, and surface relevant online content faster.',
  },
  {
    title: 'Human-led reporting',
    text: 'Technology acts as a force multiplier, but the final product is analyst-written, client-specific, and decision-focused.',
  },
  {
    title: 'Built for external intelligence support',
    text: 'The retainer model gives clients access to an intelligence capability without building an internal analyst team from scratch.',
  },
];

const notThis = [
  {
    title: 'Not a generic search report',
    text: 'Ordinary search engines often miss forums, comment sections, small blogs, buried posts, and material that requires structured OSINT methods to locate.',
  },
  {
    title: 'Not an automated alert feed',
    text: 'The value is not just collecting mentions. The value is turning relevant findings into protective intelligence that can inform decisions.',
  },
  {
    title: 'Not private investigation work',
    text: 'This service is focused on open-source information, protective intelligence, online exposure, travel intelligence, and written reporting.',
  },
];

function ExecutiveIntelligence() {
  return (
    <>
      <Headercomponent />

      <div className="container">
        <section className="hero">
          <NodeBackground />

          <div className="hero-content">
            <h1>Protective Intelligence</h1>
            <p>
              Weathervane Intel provides full-service OSINT analyst support for executives,
              public-facing leaders, family offices, company security leads, and protective firms
              that need a dedicated intelligence arm without building an in-house team.
            </p>

            <div className="hero-actions">
              <Link className="cta-button" to="/contact">
                Request Consultation
              </Link>
            </div>

            <div className="hero-proof-row">
              <span>Threat Detection</span>
              <span>Protective intelligence</span>
              <span>Online exposure review</span>
              <span>Travel risk support</span>
            </div>
          </div>

          <div className="hero-diagram">
            <div className="service-graphic">
              <div className="service-graphic-header">
                <h3>Full-Service Intelligence Support</h3>
              </div>

              <div className="service-graphic-grid">
                <div className="service-graphic-item">
                  <div className="service-graphic-left">
                    <span>Identify online exposure, threats, and relevant public activity</span>
                  </div>
                  <span className="service-arrow">01</span>
                </div>

                <div className="service-graphic-item">
                  <div className="service-graphic-left">
                    <span>Translate findings into written protective intelligence</span>
                  </div>
                  <span className="service-arrow">02</span>
                </div>

                <div className="service-graphic-item">
                  <div className="service-graphic-left">
                    <span>Support security decisions before travel, events, and public exposure</span>
                  </div>
                  <span className="service-arrow">03</span>
                </div>

                <div className="service-graphic-item">
                  <div className="service-graphic-left">
                    <span>Interface with protection teaams to inform decision making</span>
                  </div>
                  <span className="service-arrow">04</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="platform-overview">
          <span className="section-kicker">Who This Is For</span>
          <h2>Intelligence for individuals and organizations operating under exposure.</h2>
          <p>
            The service is designed for people and teams who are responsible for personal safety,
            public exposure, travel decisions, family privacy, and protective planning. It gives
            clients a recurring intelligence capability without forcing them to build an in-house team. The service integrates 
            seemlessly into your protection plan to help inform you and your team of new and emerging threats.  
          </p>

          <div className="overview-cards">
            {clientTypes.map((client) => (
              <article className="overview-card" key={client.title}>
                <h3>{client.title}</h3>
                <p>{client.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="process-deliverables-section">
          <div className="process-column">
            <span className="section-kicker">The Problem</span>
            <h2>Security decisions are weaker without a clear threat picture.</h2>
            <p>
              Many clients know they need more awareness, but they do not have a structured way to
              understand online threats, public exposure, travel risks, or the digital information
              that may affect their safety.
            </p>

            <div className="process-steps">
              {painPoints.map((point, index) => (
                <div className="process-step" key={point.title}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <div>
                    <h3>{point.title}</h3>
                    <p>{point.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="deliverables-column">
            <span className="section-kicker">Questions We Help Answer</span>
            <h2>Intelligence that supports protection.</h2>
            <p>
              The goal is not to overwhelm the client with raw information. The goal is to identify
              what matters and explain why it matters.
            </p>

            <div className="deliverables-grid">
              {intelligenceQuestions.map((question) => (
                <div className="deliverable-card" key={question}>
                  <p>{question}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="platform-overview">
          <span className="section-kicker">Services Included</span>
          <h2>A full-service OSINT analyst package.</h2>
          <p>
            Retainers combine recurring analyst support, online exposure review, threat detection,
            travel intelligence, crime mapping, reporting, and security team interface. The work is
            human-led, with proprietary technology used as a force multiplier.
          </p>

          <div className="overview-cards">
            {servicesIncluded.map((service) => (
              <article className="overview-card" key={service.title}>
                <h3>{service.title}</h3>
                <p>{service.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="platform-overview">
          <span className="section-kicker">Engagement Models</span>
          <h2>Monthly retainers scoped around the client’s exposure and security needs.</h2>
          <p>
            Engagements are scoped after consultation. The entry retainer is designed around a
            defined monthly analyst-hour allocation, recurring reporting, initial exposure review,
            travel intelligence, and ongoing protective intelligence support.
          </p>

          <div className="overview-cards">
            {retainerTiers.map((tier) => (
              <article className="overview-card" key={tier.title}>
                <span className="section-kicker">{tier.label}</span>
                <h3>{tier.title}</h3>
                <p>{tier.text}</p>

                <ul>
                  {tier.details.map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section className="process-deliverables-section">
          <div className="process-column">
            <span className="section-kicker">How It Works</span>
            <h2>From vague concern to recurring intelligence support.</h2>
            <p>
              The process begins with a private consultation and develops into a structured
              intelligence workflow for the client, advisor, or security team.
            </p>

            <div className="process-steps">
              {processSteps.map((step) => (
                <div className="process-step" key={step.title}>
                  <span>{step.number}</span>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="deliverables-column">
            <span className="section-kicker">Deliverables</span>
            <h2>Written products and briefing support.</h2>
            <p>
              Clients receive concise intelligence products, longer exposure assessments when
              needed, and briefing support for the people responsible for acting on the information.
            </p>

            <div className="deliverables-grid">
              {deliverables.map((deliverable) => (
                <div className="deliverable-card" key={deliverable.title}>
                  <h3>{deliverable.title}</h3>
                  <p>{deliverable.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="platform-overview">
          <span className="section-kicker">Why Weathervane</span>
          <h2>Analyst judgment, protective intelligence experience, and proprietary technology.</h2>
          <p>
            Weathervane is not positioned as a generic monitoring tool. The service combines human
            analysis, protective intelligence experience, OSINT methods, law enforcement background,
            and technology built to speed collection and analysis.
          </p>

          <div className="overview-cards">
            {whyWeathervane.map((item) => (
              <article className="overview-card" key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="platform-overview">
          <span className="section-kicker">Important Distinction</span>
          <h2>What this service is not.</h2>
          <p>
            This page is intentionally focused on protective intelligence, open-source information,
            travel intelligence, online exposure, and written intelligence support.
          </p>

          <div className="overview-cards">
            {notThis.map((item) => (
              <article className="overview-card" key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="cta-section">
          <h2>Need a dedicated intelligence arm?</h2>
          <p>
            Request a consultation to discuss the client profile, current concerns, travel tempo,
            public exposure, security team structure, and whether Weathervane is the right fit for
            ongoing protective intelligence support.
          </p>

          <div className="hero-actions" style={{ justifyContent: 'center' }}>
            <Link className="cta-button" to="/contact">
              Request Consultation
            </Link>

            <a
              className="cta-button"
              href="mailto:info@weathervaneintel.com?subject=Protective%20Intelligence%20Consultation"
            >
              Email Weathervane
            </a>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
}

export default ExecutiveIntelligence;
